import SeoLandingLayout from "@/components/cre/SeoLandingLayout";

const RELATED = [
  { to: "/ai-incident-management", label: "AI incident management" },
  { to: "/aiops-incident-reasoning", label: "AIOps incident reasoning" },
  { to: "/root-cause-analysis-ai", label: "Root cause analysis AI" },
  { to: "/it-downtime-reduction", label: "IT downtime reduction" },
];

const FAQ = [
  {
    q: "What is major incident management?",
    a: "Major incident management is the coordinated response to high-impact IT incidents that affect customers, revenue, or critical business services. CREAIOps adds an AI reasoning layer that helps incident managers coordinate cross-functional teams faster.",
  },
  {
    q: "Does CREAIOps replace the incident manager role?",
    a: "No. CREAIOps augments incident managers with structured reasoning, ranked hypotheses, and approval-ready artefacts. The human incident manager continues to own coordination, communication, and final decisions.",
  },
  {
    q: "How does CREAIOps reduce bridge-call noise?",
    a: "By giving every team the same ranked reasoning view, CREAIOps replaces repeated status questions with a shared source of truth. Engineers focus on validation and execution instead of re-explaining context.",
  },
  {
    q: "Can CREAIOps be used for sev-1 and sev-2 incidents?",
    a: "Yes. CREAIOps is designed for major incidents where cross-team reasoning, ranked actions, and stakeholder updates matter most.",
  },
  {
    q: "How does CREAIOps handle stakeholder communications?",
    a: "CREAIOps generates a stakeholder-ready draft tuned for leadership and one for engineers, both derived from the same reasoning trail — keeping technical and executive communication aligned.",
  },
];

export default function MajorIncidentManagement() {
  return (
    <SeoLandingLayout
      slug="major-incident-management"
      metaTitle="Major Incident Management with AI Reasoning | CREAIOps"
      metaDescription="CREAIOps accelerates major incident management for ITSM, SRE, and IT operations leaders. Cross-functional reasoning, ranked hypotheses, stakeholder updates, and human-approved runbooks — without auto-remediation."
      h1="Major incident management, augmented by AI reasoning"
      intro="During a major incident, leaders need clarity, not more dashboards. CREAIOps gives incident managers and on-call leads a single ranked reasoning view across app, infra, network, security, database, and business impact — so coordination, communication, and decisions all move faster."
      problem={{
        title:
          "Major incident bridges burn time on status updates instead of decisions",
        body:
          "When an incident escalates, the bridge fills with people asking the same questions: what's broken, who owns it, what's the impact, what are we trying. The incident manager becomes a human router. The result is slower restoration and inconsistent stakeholder communication.",
        points: [
          "Multiple specialists hold partial views of the same incident",
          "Leadership and engineering hear different versions of the same update",
          "Reasoning happens in private DMs, not in the shared workspace",
          "Approval and governance steps add minutes when seconds matter",
        ],
      }}
      howWeHelp={{
        title:
          "A shared AI reasoning workspace built for the major-incident bridge",
        body:
          "CREAIOps gives the major-incident bridge a shared, structured view. Multi-agent reasoning publishes hypotheses, ranked actions, and stakeholder drafts — and human approval remains the source of truth for any change.",
        items: [
          {
            title: "Cross-domain reasoning, one view",
            desc: "Application, infrastructure, network, security, database, and business-impact lenses converge into a single ranked picture.",
          },
          {
            title: "Stakeholder + engineering narratives",
            desc: "Generate both an executive update and a technical narrative from the same reasoning trail.",
          },
          {
            title: "Ranked safe actions",
            desc: "Prioritised, human-reviewable next steps that respect blast radius and rollback.",
          },
          {
            title: "Approval-ready governance",
            desc: "Every recommendation is gated by explicit human approval and captured in an audit trail.",
          },
        ],
      }}
      benefits={[
        "Faster cross-team coordination during sev-1 and sev-2 incidents",
        "Aligned communication between leadership and engineers",
        "Shorter time to a validated hypothesis",
        "Less noise in war rooms and bridge calls",
        "Stronger governance footprint without slowing response",
      ]}
      useCase={{
        title:
          "A telco uses CREAIOps to coordinate a regional service outage",
        body:
          "During a multi-region outage, the major incident manager submits an anonymized incident brief into CREAIOps. Within minutes, the workspace shows ranked hypotheses, an executive draft, and a recommended action list — all reviewed by senior engineers and approved by the change manager.",
        steps: [
          "Major incident manager submits an anonymized brief",
          "Reasoning agents post ranked hypotheses by domain",
          "Stakeholder and engineering drafts are generated in parallel",
          "Action list is reviewed by senior engineering and change governance",
          "Approved runbook is executed by the on-call team",
          "Audit trail is exported for problem management",
        ],
      }}
      faq={FAQ}
      related={RELATED}
    />
  );
}
